/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/tstep.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Creates groups of values - buckets - out of a `@timestamp` attribute using either a fixed step width
 * or a target bucket count.
 * Unlike `TBUCKET`,
 * which aligns buckets to calendar boundaries, TSTEP uses a fixed-width UTC grid anchored at the start
 * of the query range. Each bucket is labeled by its right boundary.
 * When a target bucket count is provided, TSTEP derives a fixed step width from the query range.
 * The derived step is rounded up so that the result uses no more than the target number of buckets.
 *
 * When using ES|QL in Kibana, the range can be derived automatically from the
 * `@timestamp` filter
 * that Kibana adds to the query.
 *
 * @example
 * FROM sample_data
 * | STATS min = MIN(@timestamp), max = MAX(@timestamp) BY bucket = TSTEP(1 hour)
 * | SORT bucket
 *
 * @example
 * FROM sample_data
 * | STATS min = MIN(@timestamp), max = MAX(@timestamp) BY bucket = TSTEP(1 hour, "2023-10-23T12:15:00.000Z", "2023-10-23T13:55:01.543Z")
 * | SORT bucket
 *
 * @example
 * FROM sample_data
 * | STATS min = MIN(@timestamp), max = MAX(@timestamp) BY bucket = TSTEP(2, "2023-10-23T12:15:00.000Z", "2023-10-23T14:15:00.000Z")
 * | SORT bucket
 */
const definition: FunctionDefinition = {
  type: 'grouping',
  name: 'tstep',
  signatures: [
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'step',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'from',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'to',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
  ],
  preview: false,
  snapshotOnly: true,
};

export default definition;
